import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Package, Plus, Search, QrCode, Wrench, AlertTriangle, CheckCircle, 
  Calendar, MapPin, ChevronRight, Printer, Filter, MoreVertical, Trash2, Edit, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import api from '../services/api';

// Equipment categories (matching backend EquipmentCategory enum)
const CATEGORIES = [
  { value: 'GSE', label: 'Ground Support Equipment' },
  { value: 'fire_safety', label: 'Fire Safety' },
  { value: 'emergency', label: 'Emergency Equipment' },
  { value: 'cabin', label: 'Cabin Equipment' },
  { value: 'cargo', label: 'Cargo Equipment' },
  { value: 'maintenance_tools', label: 'Maintenance Tools' },
  { value: 'communication', label: 'Communication' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'other', label: 'Other' }
];

// Equipment statuses (matching backend EquipmentStatus enum)
const STATUSES = [
  { value: 'operational', label: 'Operational', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { value: 'maintenance', label: 'In Maintenance', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { value: 'out_of_service', label: 'Out of Service', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { value: 'retired', label: 'Retired', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
];

const getStatusInfo = (status) => STATUSES.find(s => s.value === status) || STATUSES[0];

export default function EquipmentPage() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    equipment_type: '',
    barcode: '',
    barcode_type: 'qr',
    serial_number: '',
    category: '',
    manufacturer: '',
    model: '',
    location: '',
    department: '',
    inspection_interval_days: 30,
    notes: ''
  });
  
  // Fetch equipment list
  const { data: equipment = [], isLoading } = useQuery({
    queryKey: ['equipment', statusFilter, categoryFilter],
    queryFn: async () => {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const response = await api.get('/equipment', { params });
      return response.data;
    }
  });
  
  // Fetch stats
  const { data: stats = {} } = useQuery({
    queryKey: ['equipment-stats'],
    queryFn: async () => {
      const response = await api.get('/equipment/stats');
      return response.data;
    }
  });
  
  // Create equipment mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/equipment', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['equipment']);
      queryClient.invalidateQueries(['equipment-stats']);
      setCreateDialogOpen(false);
      resetForm();
      toast.success('Equipment registered successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to register equipment');
    }
  });
  
  // Delete equipment mutation
  const deleteMutation = useMutation({
    mutationFn: async (equipmentId) => {
      const response = await api.delete(`/equipment/${equipmentId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['equipment']);
      queryClient.invalidateQueries(['equipment-stats']);
      toast.success('Equipment retired successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to retire equipment');
    }
  });
  
  const resetForm = () => {
    setFormData({
      name: '',
      equipment_type: '',
      barcode: '',
      barcode_type: 'qr',
      serial_number: '',
      category: '',
      manufacturer: '',
      model: '',
      location: '',
      department: '',
      inspection_interval_days: 30,
      notes: ''
    });
  };
  
  const handleCreate = () => {
    if (!formData.name || !formData.equipment_type || !formData.category || !formData.barcode) {
      toast.error('Please fill in required fields: Name, Type, Category, and Barcode');
      return;
    }
    createMutation.mutate(formData);
  };
  
  const generateBarcode = () => {
    const prefix = 'AG';
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    const barcode = `${prefix}${timestamp}${random}`;
    setFormData(prev => ({ ...prev, barcode }));
  };
  
  const filteredEquipment = equipment.filter(item => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        item.name?.toLowerCase().includes(search) ||
        item.barcode?.toLowerCase().includes(search) ||
        item.serial_number?.toLowerCase().includes(search) ||
        item.location?.toLowerCase().includes(search)
      );
    }
    return true;
  });
  
  const viewEquipmentDetail = async (item) => {
    try {
      const response = await api.get(`/equipment/${item.id}`);
      setSelectedEquipment(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load equipment details');
    }
  };
  
  const printBarcode = (item) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Equipment Barcode - ${item.name}</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
            .barcode-container { border: 2px solid #333; padding: 20px; display: inline-block; }
            .barcode { font-family: 'Libre Barcode 128', monospace; font-size: 48px; }
            .label { font-size: 14px; margin-top: 10px; }
            .name { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
            @media print { body { margin: 0; } }
          </style>
          <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128&display=swap" rel="stylesheet">
        </head>
        <body>
          <div class="barcode-container">
            <div class="name">${item.name}</div>
            <div class="barcode">${item.barcode}</div>
            <div class="label">${item.barcode}</div>
            <div class="label">${item.location}</div>
          </div>
          <script>
            setTimeout(() => { window.print(); window.close(); }, 500);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };
  
  return (
    <div className="space-y-6" data-testid="equipment-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-2xl font-bold font-barlow", isDark ? "text-white" : "text-gray-900")}>
            Equipment Management
          </h1>
          <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-gray-600")}>
            Register, track, and manage equipment with barcode scanning
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="gap-2" data-testid="register-equipment-btn">
          <Plus className="h-4 w-4" />
          Register Equipment
        </Button>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>Total Equipment</p>
                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>{stats.total || 0}</p>
              </div>
              <Package className="h-8 w-8 text-sky-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>Serviceable</p>
                <p className="text-2xl font-bold text-emerald-500">{stats.serviceable || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>In Maintenance</p>
                <p className="text-2xl font-bold text-amber-500">{stats.in_maintenance || 0}</p>
              </div>
              <Wrench className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>Inspection Due</p>
                <p className="text-2xl font-bold text-yellow-500">{stats.inspection_due || 0}</p>
              </div>
              <Calendar className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>Unserviceable</p>
                <p className="text-2xl font-bold text-red-500">{stats.unserviceable || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <div className={cn("flex flex-wrap gap-4 p-4 rounded-lg", isDark ? "bg-slate-800/50 border border-slate-700" : "bg-gray-50 border border-gray-200")}>
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, barcode, serial number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn("pl-10", isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300")}
              data-testid="equipment-search"
            />
          </div>
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={cn("w-[180px]", isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300")}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className={cn("w-[200px]", isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300")}>
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Equipment List */}
      <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
        <CardHeader>
          <CardTitle className={cn(isDark ? "text-white" : "text-gray-900")}>
            Equipment Inventory ({filteredEquipment.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-2 border-sky-500 border-t-transparent rounded-full"></div>
            </div>
          ) : filteredEquipment.length === 0 ? (
            <div className={cn("text-center py-12", isDark ? "text-slate-400" : "text-gray-500")}>
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No equipment found</p>
              <Button variant="outline" className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                Register First Equipment
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={cn("border-b", isDark ? "border-slate-700" : "border-gray-200")}>
                    <th className={cn("text-left py-3 px-4 font-semibold", isDark ? "text-slate-300" : "text-gray-700")}>Equipment</th>
                    <th className={cn("text-left py-3 px-4 font-semibold", isDark ? "text-slate-300" : "text-gray-700")}>Barcode</th>
                    <th className={cn("text-left py-3 px-4 font-semibold", isDark ? "text-slate-300" : "text-gray-700")}>Category</th>
                    <th className={cn("text-left py-3 px-4 font-semibold", isDark ? "text-slate-300" : "text-gray-700")}>Location</th>
                    <th className={cn("text-left py-3 px-4 font-semibold", isDark ? "text-slate-300" : "text-gray-700")}>Status</th>
                    <th className={cn("text-left py-3 px-4 font-semibold", isDark ? "text-slate-300" : "text-gray-700")}>Next Inspection</th>
                    <th className={cn("text-right py-3 px-4 font-semibold", isDark ? "text-slate-300" : "text-gray-700")}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEquipment.map((item) => {
                    const statusInfo = getStatusInfo(item.status);
                    const isOverdue = item.next_inspection_date && new Date(item.next_inspection_date) < new Date();
                    
                    return (
                      <tr 
                        key={item.id} 
                        className={cn("border-b hover:bg-slate-800/30 cursor-pointer", isDark ? "border-slate-700/50" : "border-gray-100 hover:bg-gray-50")}
                        onClick={() => viewEquipmentDetail(item)}
                        data-testid={`equipment-row-${item.id}`}
                      >
                        <td className="py-3 px-4">
                          <div>
                            <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>{item.name}</p>
                            {item.serial_number && (
                              <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>SN: {item.serial_number}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <QrCode className="h-4 w-4 text-sky-500" />
                            <code className={cn("text-sm font-mono", isDark ? "text-slate-300" : "text-gray-700")}>{item.barcode}</code>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={cn("text-sm capitalize", isDark ? "text-slate-400" : "text-gray-600")}>
                            {item.category?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-slate-400" />
                            <span className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>{item.location}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={cn("text-xs border", statusInfo.color)}>
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {item.next_inspection_date ? (
                            <span className={cn("text-sm", isOverdue ? "text-red-400 font-medium" : isDark ? "text-slate-400" : "text-gray-600")}>
                              {new Date(item.next_inspection_date).toLocaleDateString()}
                              {isOverdue && ' (Overdue)'}
                            </span>
                          ) : (
                            <span className={cn("text-sm", isDark ? "text-slate-500" : "text-gray-400")}>-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => viewEquipmentDetail(item)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => printBarcode(item)}>
                                <Printer className="h-4 w-4 mr-2" />
                                Print Barcode
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-400"
                                onClick={() => {
                                  if (confirm('Are you sure you want to retire this equipment?')) {
                                    deleteMutation.mutate(item.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Retire
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Create Equipment Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className={cn("max-w-2xl", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-white" : "text-gray-900")}>Register New Equipment</DialogTitle>
            <DialogDescription>Add equipment to the inventory with barcode tracking</DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Equipment Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Fire Extinguisher A1"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
                data-testid="equipment-name-input"
              />
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Equipment Type *</Label>
              <Input
                value={formData.equipment_type}
                onChange={(e) => setFormData(prev => ({ ...prev, equipment_type: e.target.value }))}
                placeholder="e.g., Fire Extinguisher, Tow Bar"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
                data-testid="equipment-type-input"
              />
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Barcode *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.barcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, barcode: e.target.value }))}
                  placeholder="Auto-generated or custom"
                  className={cn(isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
                  data-testid="equipment-barcode-input"
                />
                <Button variant="outline" onClick={generateBarcode} className="shrink-0">
                  <QrCode className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                <SelectTrigger className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Serial Number</Label>
              <Input
                value={formData.serial_number}
                onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                placeholder="Manufacturer serial number"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
              />
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Hangar A, Bay 3"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
                data-testid="equipment-location-input"
              />
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Department</Label>
              <Input
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                placeholder="e.g., Ground Operations"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
              />
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Manufacturer</Label>
              <Input
                value={formData.manufacturer}
                onChange={(e) => setFormData(prev => ({ ...prev, manufacturer: e.target.value }))}
                placeholder="Manufacturer name"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
              />
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Model</Label>
              <Input
                value={formData.model}
                onChange={(e) => setFormData(prev => ({ ...prev, model: e.target.value }))}
                placeholder="Model number"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
              />
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Inspection Interval (Days)</Label>
              <Input
                type="number"
                value={formData.inspection_interval_days}
                onChange={(e) => setFormData(prev => ({ ...prev, inspection_interval_days: parseInt(e.target.value) || 30 }))}
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
              />
            </div>
            
            <div className="col-span-2">
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional details about the equipment"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending} data-testid="save-equipment-btn">
              {createMutation.isPending ? 'Registering...' : 'Register Equipment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Equipment Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className={cn("max-w-3xl max-h-[80vh] overflow-y-auto", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          {selectedEquipment && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className={cn("text-xl", isDark ? "text-white" : "text-gray-900")}>
                      {selectedEquipment.name}
                    </DialogTitle>
                    <div className="flex items-center gap-3 mt-2">
                      <Badge className={cn("text-xs border", getStatusInfo(selectedEquipment.status).color)}>
                        {getStatusInfo(selectedEquipment.status).label}
                      </Badge>
                      <code className={cn("text-sm font-mono", isDark ? "text-slate-400" : "text-gray-600")}>
                        {selectedEquipment.barcode}
                      </code>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => printBarcode(selectedEquipment)}>
                    <Printer className="h-4 w-4 mr-2" />
                    Print Label
                  </Button>
                </div>
              </DialogHeader>
              
              <Tabs defaultValue="details" className="mt-4">
                <TabsList>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="history">Inspection History</TabsTrigger>
                  <TabsTrigger value="issues">Issues</TabsTrigger>
                </TabsList>
                
                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                      <Label className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>Equipment Type</Label>
                      <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                        {selectedEquipment.equipment_type || '-'}
                      </p>
                    </div>
                    <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                      <Label className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>Category</Label>
                      <p className={cn("font-medium capitalize", isDark ? "text-white" : "text-gray-900")}>
                        {selectedEquipment.category?.replace('_', ' ')}
                      </p>
                    </div>
                    <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                      <Label className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>Location</Label>
                      <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                        {selectedEquipment.location || '-'}
                      </p>
                    </div>
                    <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                      <Label className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>Department</Label>
                      <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                        {selectedEquipment.department || '-'}
                      </p>
                    </div>
                    <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                      <Label className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>Serial Number</Label>
                      <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                        {selectedEquipment.serial_number || '-'}
                      </p>
                    </div>
                    <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                      <Label className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>Manufacturer / Model</Label>
                      <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                        {selectedEquipment.manufacturer || '-'} {selectedEquipment.model && `/ ${selectedEquipment.model}`}
                      </p>
                    </div>
                    <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                      <Label className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>Last Inspection</Label>
                      <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                        {selectedEquipment.last_inspection_date 
                          ? new Date(selectedEquipment.last_inspection_date).toLocaleDateString()
                          : 'Never'}
                      </p>
                    </div>
                    <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                      <Label className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>Next Inspection</Label>
                      <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                        {selectedEquipment.next_inspection_date 
                          ? new Date(selectedEquipment.next_inspection_date).toLocaleDateString()
                          : '-'}
                      </p>
                    </div>
                  </div>
                  
                  {selectedEquipment.notes && (
                    <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                      <Label className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>Notes</Label>
                      <p className={cn("mt-1", isDark ? "text-slate-300" : "text-gray-700")}>
                        {selectedEquipment.notes}
                      </p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="history" className="mt-4">
                  {selectedEquipment.inspection_history?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedEquipment.inspection_history.map((inspection, idx) => (
                        <div key={idx} className={cn("p-3 rounded-lg border", isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200")}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                                Inspection by {inspection.inspector_name}
                              </p>
                              <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                                {new Date(inspection.inspection_date).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className={cn(
                              "text-xs",
                              inspection.result === 'pass' 
                                ? "bg-emerald-500/20 text-emerald-400" 
                                : "bg-red-500/20 text-red-400"
                            )}>
                              {inspection.result}
                            </Badge>
                          </div>
                          {inspection.notes && (
                            <p className={cn("text-sm mt-2", isDark ? "text-slate-300" : "text-gray-600")}>
                              {inspection.notes}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={cn("text-center py-8", isDark ? "text-slate-500" : "text-gray-400")}>
                      <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No inspection history</p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="issues" className="mt-4">
                  {selectedEquipment.issues?.filter(i => i.status !== 'resolved').length > 0 ? (
                    <div className="space-y-3">
                      {selectedEquipment.issues.filter(i => i.status !== 'resolved').map((issue, idx) => (
                        <div key={idx} className={cn("p-3 rounded-lg border", isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200")}>
                          <div className="flex items-center justify-between">
                            <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                              {issue.description}
                            </p>
                            <Badge className={cn(
                              "text-xs",
                              issue.severity === 'critical' ? "bg-red-500/20 text-red-400" :
                              issue.severity === 'major' ? "bg-amber-500/20 text-amber-400" :
                              "bg-yellow-500/20 text-yellow-400"
                            )}>
                              {issue.severity}
                            </Badge>
                          </div>
                          <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-gray-500")}>
                            Reported {new Date(issue.reported_at).toLocaleDateString()} by {issue.reported_by_name}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={cn("text-center py-8", isDark ? "text-slate-500" : "text-gray-400")}>
                      <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No open issues</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
